import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";

const STORAGE_KEY = "nb-cookie-consent";

const optionalTypes = [
  { key: "functional", title: "Functional Cookies", desc: "Enable enhanced functionality and personalisation. Disabling them may cause some features to work incorrectly." },
  { key: "analytical", title: "Analytical Cookies", desc: "Help us understand how visitors use the site so we can analyse performance and improve the experience." },
  { key: "marketing", title: "Marketing Cookies", desc: "Used to deliver relevant content and advertising on this site and on third-party sites." },
];

const defaults = { functional: false, analytical: false, marketing: false };

const CookieSettings = ({ open, onClose }) => {
  const [prefs, setPrefs] = useState(defaults);

  useEffect(() => {
    if (!open) return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      setPrefs({
        functional: !!saved.functional,
        analytical: !!saved.analytical,
        marketing: !!saved.marketing,
      });
    } catch {
      setPrefs(defaults);
    }
  }, [open]);

  if (!open) return null;

  const save = (next) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ essential: true, ...next, savedAt: new Date().toISOString() })
    );
    toast.success("Your cookie preferences have been saved.");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="cookie-settings-modal">
      <div className="absolute inset-0 bg-[#0B132B]/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl p-6 md:p-10 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          data-testid="cookie-close-button"
          aria-label="Close cookie settings"
          className="absolute top-5 right-5 text-[#64748B] hover:text-[#0F172A] transition-colors duration-200"
        >
          <X size={20} />
        </button>
        <h2 className="font-display text-xl md:text-2xl font-bold text-[#0F172A]">Cookie settings</h2>
        <p className="mt-3 text-sm text-[#475569] leading-relaxed">
          We use cookies to run the site, understand usage, and improve your experience. You choose what to
          allow. See our{" "}
          <Link to="/privacy" onClick={onClose} className="text-[#FF5E1A] underline underline-offset-2">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="mt-7 border-t border-[#E6E2D8]">
          <div className="flex items-start justify-between gap-6 py-6 border-b border-[#E6E2D8]">
            <div>
              <p className="text-sm font-bold text-[#0F172A]">
                Essential Cookies <span className="font-medium text-[#64748B]">(always on)</span>
              </p>
              <p className="mt-1.5 text-sm text-[#64748B] leading-relaxed">
                Required for the website to function. Some services or features may not be available if disabled.
              </p>
            </div>
            <Switch checked disabled data-testid="cookie-toggle-essential" className="mt-0.5" />
          </div>
          {optionalTypes.map((t) => (
            <div key={t.key} className="flex items-start justify-between gap-6 py-6 border-b border-[#E6E2D8]">
              <div>
                <p className="text-sm font-bold text-[#0F172A]">{t.title}</p>
                <p className="mt-1.5 text-sm text-[#64748B] leading-relaxed">{t.desc}</p>
              </div>
              <Switch
                checked={prefs[t.key]}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, [t.key]: v }))}
                data-testid={`cookie-toggle-${t.key}`}
                className="mt-0.5"
              />
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button
            onClick={() => save(defaults)}
            data-testid="cookie-reject-all"
            className="btn-outline-light justify-center"
          >
            Reject all
          </button>
          <button
            onClick={() => save(prefs)}
            data-testid="cookie-save-selection"
            className="btn-primary justify-center"
          >
            Save my selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings;
